import * as DialogPrimitive from '@radix-ui/react-dialog';
import React from 'react';

import { OAUTH_PROVIDER_NAMES } from '@/features/auth/components/OAuthProviderLogo';
import type { OAuthProvider } from '@/lib/api/auth';
import { cn } from '@/lib/utils';

import type { useEoaProvisioning } from '../hooks/useEoaProvisioning';
import type { OnboardingStep } from '../hooks/useOnboardingFlow';
import { ONBOARDING_STEP_ORDER } from '../hooks/useOnboardingFlow';
import type { useSnappyOnboarding } from '../hooks/useSnappyOnboarding';

import StepProgress from './StepProgress';
import FundingStep from './steps/FundingStep';
import LinkSocialStep from './steps/LinkSocialStep';
import ProvisionStep from './steps/ProvisionStep';
import TermsStep from './steps/TermsStep';
import VoucherQueueStep from './steps/VoucherQueueStep';
import WalletSetupStep from './steps/WalletSetupStep';
import WelcomeStep from './steps/WelcomeStep';

interface OnboardingDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    step: OnboardingStep;
    maxReachedIndex: number;
    onStepSelect: (index: number) => void;
    isSocialAuthed: boolean;
    isWalletAuthed: boolean;
    /**
     * Association run: the social login this account is being created for. The
     * wallet step then asks for the owner EOA even though a social session
     * exists, and the provision step says what the extra leg is for.
     */
    associateSocialProvider: OAuthProvider | null;
    onWelcomeNext: () => void;
    onAcceptTerms: () => void;
    onDeclineTerms: () => void;
    onWalletContinue: () => void;
    onVoucherContinue: () => void;
    voucher: ReturnType<typeof useSnappyOnboarding>;
    /** Wallet-only users create their own wallets in the provision step. */
    needsProvisioning: boolean;
    provisioning: ReturnType<typeof useEoaProvisioning>;
    hasCustodialWallet: boolean;
    onProvisionStart: () => void;
    /** Leaves the signed-in account and continues as the connected one. */
    onProvisionSwitchAccount: () => void | Promise<void>;
    /** Re-enters the admission queue after the setup window expired. */
    onProvisionRejoin: () => void;
    onProvisionContinue: () => void;
    /** Continue to funding is waiting on the wallet's signature. */
    provisionContinuing?: boolean;
    /** The last Continue to funding attempt failed; shown on the step. */
    provisionContinueError?: string | null;
    onFinish: (options?: { openDeposit?: boolean }) => void;
    /**
     * A social login added from the funding step that still has to be linked to
     * the account: the funding step gives way to the link step until it is.
     */
    socialLink?: {
        providerName: string;
        ownerAddress: string;
        accountWallet: string | null;
        isWorking: boolean;
        error: string | null;
        onLink: () => void;
    } | null;
}

/**
 * First-login onboarding: welcome → terms gate → Snappy Wallet setup →
 * funding info. Bottom sheet on mobile, centered card on desktop.
 */
const OnboardingDialog: React.FC<OnboardingDialogProps> = ({
    open,
    onOpenChange,
    step,
    maxReachedIndex,
    onStepSelect,
    isSocialAuthed,
    isWalletAuthed,
    associateSocialProvider,
    onWelcomeNext,
    onAcceptTerms,
    onDeclineTerms,
    onWalletContinue,
    onVoucherContinue,
    voucher,
    needsProvisioning,
    provisioning,
    hasCustodialWallet,
    onProvisionStart,
    onProvisionSwitchAccount,
    onProvisionRejoin,
    onProvisionContinue,
    provisionContinuing = false,
    provisionContinueError = null,
    onFinish,
    socialLink = null,
}) => {
    const activeIndex = ONBOARDING_STEP_ORDER.indexOf(step);
    const associateProviderName = associateSocialProvider
        ? OAUTH_PROVIDER_NAMES[associateSocialProvider]
        : null;

    // On step change the previous step's controls unmount and focus falls to
    // the body — move it to the new step's container so keyboard/SR users
    // land on the fresh content (skipping the initial mount, where Radix
    // handles focus).
    const stepContainerRef = React.useRef<HTMLDivElement>(null);
    const prevStepRef = React.useRef(step);
    React.useEffect(() => {
        if (prevStepRef.current !== step) {
            stepContainerRef.current?.focus();
        }
        prevStepRef.current = step;
    }, [step]);

    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
                <DialogPrimitive.Content
                    className={cn(
                        'fixed z-50 flex flex-col outline-none',
                        'border border-border bg-card',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out',
                        'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
                        'data-[state=open]:duration-300 data-[state=open]:ease-out',
                        'data-[state=closed]:duration-200 data-[state=closed]:ease-in',
                        'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-xl',
                        'max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom',
                        'md:inset-auto md:left-1/2 md:top-1/2 md:w-110 md:max-h-[85vh] md:translate-x-[-50%] md:translate-y-[-50%] md:rounded-xl',
                        'md:data-[state=open]:zoom-in-95 md:data-[state=closed]:zoom-out-95',
                    )}
                >
                    <DialogPrimitive.Title className="sr-only">
                        Getting started with Snappy Trade
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="sr-only">
                        First-login setup: welcome, terms acceptance, Snappy
                        Wallet setup and funding overview.
                    </DialogPrimitive.Description>

                    <div className="px-6 pb-4 pt-5">
                        <StepProgress
                            activeIndex={activeIndex}
                            total={ONBOARDING_STEP_ORDER.length}
                            maxReachedIndex={maxReachedIndex}
                            onStepSelect={onStepSelect}
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        <div
                            key={step}
                            ref={stepContainerRef}
                            tabIndex={-1}
                            className="animate-in fade-in-0 slide-in-from-right-4 duration-300 outline-none"
                        >
                            {step === 'welcome' && (
                                <WelcomeStep onNext={onWelcomeNext} />
                            )}
                            {step === 'terms' && (
                                <TermsStep
                                    onAccept={onAcceptTerms}
                                    onDecline={onDeclineTerms}
                                />
                            )}
                            {step === 'wallet' && (
                                <WalletSetupStep
                                    isSocialAuthed={isSocialAuthed}
                                    isWalletAuthed={isWalletAuthed}
                                    requiresOwnerWallet={
                                        !!associateSocialProvider
                                    }
                                    associateProviderName={
                                        associateProviderName
                                    }
                                    onContinue={onWalletContinue}
                                />
                            )}
                            {step === 'voucher' && (
                                <VoucherQueueStep
                                    status={voucher.status}
                                    busy={voucher.busy}
                                    error={voucher.error}
                                    onRedeem={voucher.redeem}
                                    // wallet-only users take the lane here and
                                    // sign for the wallets in the next step
                                    onReady={
                                        needsProvisioning
                                            ? voucher.confirmReadyOnly
                                            : voucher.ready
                                    }
                                    onRejoin={voucher.rejoin}
                                    onContinue={onVoucherContinue}
                                />
                            )}
                            {step === 'provision' && (
                                <ProvisionStep
                                    phase={provisioning.phase}
                                    anchorChainId={provisioning.anchorChainId}
                                    failure={provisioning.failure}
                                    failedLeg={provisioning.failedLeg}
                                    isRunning={provisioning.isRunning}
                                    hasStarted={provisioning.hasStarted}
                                    detachedProvisioning={
                                        provisioning.detachedProvisioning
                                    }
                                    authorizationWalletReady={
                                        provisioning.authorizationWalletReady
                                    }
                                    tradingWalletReady={
                                        hasCustodialWallet ||
                                        provisioning.tradingWalletReady ||
                                        voucher.status.state === 'completed'
                                    }
                                    socialProviderName={associateProviderName}
                                    mismatch={provisioning.mismatch}
                                    onStart={onProvisionStart}
                                    onSwitchAccount={onProvisionSwitchAccount}
                                    onRejoin={onProvisionRejoin}
                                    onContinue={onProvisionContinue}
                                    isContinuing={provisionContinuing}
                                    continueError={provisionContinueError}
                                />
                            )}
                            {step === 'funding' && socialLink && (
                                <LinkSocialStep {...socialLink} />
                            )}
                            {step === 'funding' && !socialLink && (
                                <FundingStep
                                    onDeposit={() =>
                                        onFinish({ openDeposit: true })
                                    }
                                    canAddSocialLogin={!isSocialAuthed}
                                />
                            )}
                        </div>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};

export default OnboardingDialog;
